import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { Translate } from "../i18n/hooks.ts";
import { geometriesOfLiveEntities, drawnFeature } from "../model/gisLayer.ts";
import { approximateGeometryAreaKm2 } from "../model/gisLayer.ts";
import { disambiguateMitecoNames, type MitecoBasinProperties } from "../model/mitecoLayers.ts";
import {
  importAquifersFromGeo,
  importOutletPointsFromGeo,
  importAdditionalSubBasins,
  type GisColumnMapping,
  type SubBasinColumnMapping,
} from "../model/projectEntities.ts";
import type { MitecoOrigin } from "../model/storage.ts";
import type { DrawingTool } from "./drawingTools.ts";
import type { State } from "./appState.ts";

/**
 * The three ways new sub-basins/outlet points/aquifers get added to an
 * ALREADY-OPEN project (manual entry is a fourth, but that one lives
 * entirely inside SubBasinsPanel.tsx via addManualSubBasin, never touches
 * App.tsx's own state): importing from MITECO's official layer, importing
 * an uploaded GIS file, and hand-drawing on the map. Extracted together
 * (rather than as three separate hooks) because they cross-reference each
 * other in their own comments and share the exact same underlying
 * `importAdditionalSubBasins`/`importOutletPointsFromGeo`/
 * `importAquifersFromGeo` calls — see project_evalhid_code_audit.md for why
 * App.tsx is being split into pieces like this one at a time.
 *
 * The state transitions themselves are pulled out as named, exported
 * functions (rather than staying as inline `setState` closures) so they're
 * directly unit-testable without a React environment.
 *
 * Takes `setState`/`t` as parameters rather than owning its own state: all
 * three of these read/write the SAME `state` that the rest of App.tsx
 * (calculation, panels, persistence...) also reads/writes, so `state`
 * itself has to stay owned one level up.
 */

/**
 * SubBasinsPanel.tsx's "Add from MITECO" button — on/off, defaulting to the
 * basins layer (the smaller of the two, cheaper first paint). Mutually
 * exclusive with drawing (clears it on the way in, same as
 * `withDrawingStarted` clears this on the way in) — both bind their own
 * click handling on the map and would otherwise fight over the same click.
 * Reachable from card view too (SubBasinsPanel.tsx no longer hides the
 * button there) — turning it ON switches to map view automatically, since
 * there's nothing to click on otherwise; turning it back OFF leaves the
 * view exactly as the user left it.
 */
export function withMitecoModeToggled(current: State): State {
  return current.phase === "loaded"
    ? {
        ...current,
        mitecoMode: current.mitecoMode ? null : { layer: "basins", selected: new Map() },
        drawingMode: null,
        pendingDrawing: null,
        mapView: current.mitecoMode ? current.mapView : true,
      }
    : current;
}

export function withMitecoLayer(current: State, layer: "basins" | "subBasins"): State {
  return current.phase === "loaded" && current.mitecoMode
    ? { ...current, mitecoMode: { layer, selected: new Map() } }
    : current;
}

export function withMitecoSelected(current: State, selected: Map<string, GeoJSON.Feature>): State {
  return current.phase === "loaded" && current.mitecoMode
    ? { ...current, mitecoMode: { ...current.mitecoMode, selected } }
    : current;
}

/**
 * Commits the whole `mitecoMode.selected` working set as real sub-basins in
 * one go — same underlying `importAdditionalSubBasins` as
 * `withSubBasinsRecoveredFromGis` below, but a separate transition: this
 * one is genuinely new to the project, not a recovery, and the caller
 * already has the name worked out (each feature's own `name` property)
 * and the area computed here (MITECO's own area field isn't shipped — see
 * mitecoLayers.ts — approximateGeometryAreaKm2() instead), so there's no
 * column mapping to choose. Turns the mode off afterward, same as
 * finishing a hand-drawn geometry turns drawingMode off.
 *
 * No duplicate check against the project's existing GEOMETRY (unlike
 * `withSubBasinsRecoveredFromGis`): MITECO's polygons are independently
 * simplified from whatever shapefile the project itself was built from, so
 * they'd essentially never match byte-for-byte. `basinCode` (+ which layer)
 * is the only reliable key instead — filtered here as a backstop even
 * though MitecoSelectableLayer.tsx already keeps an already-added feature
 * out of `selected` in the first place (see `mode.alreadyAdded`). Every
 * newly-created sub-basin's origin is recorded into `mitecoOrigin` so the
 * NEXT "Add from MITECO" round (this session or a future one, it's
 * persisted) knows to skip it too.
 *
 * `disambiguateMitecoNames` (mitecoLayers.ts) resolves any name collision
 * before importing — see its own comment for why MITECO's own names
 * collide so often.
 */
export function withSelectedMitecoAdded(current: State, t: Translate): State {
  if (current.phase !== "loaded" || !current.mitecoMode) return current;
  const { layer } = current.mitecoMode;
  const alreadyAdded = new Set(
    Object.values(current.mitecoOrigin)
      .filter((o) => o.layer === layer)
      .map((o) => o.basinCode)
  );
  const rawFeatures = Array.from(current.mitecoMode.selected.values()).filter(
    (f) => !alreadyAdded.has((f.properties as MitecoBasinProperties).basinCode)
  );
  if (rawFeatures.length === 0) return { ...current, mitecoMode: null };
  const existingNames = new Set(current.loadedProject.project.subBasins.map((c) => c.description));
  const features = disambiguateMitecoNames(rawFeatures, existingNames);
  const geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: features.map((f) => ({
      ...f,
      properties: {
        name: f.properties.name,
        basinCode: f.properties.basinCode,
        area: approximateGeometryAreaKm2(f.geometry),
      },
    })),
  };
  const beforeCount = current.loadedProject.project.subBasins.length;
  const { project: p, newLayer } = importAdditionalSubBasins(
    current.loadedProject.project,
    geojson,
    { nameField: "name", areaField: "area" },
    t
  );
  const newSubBasins = p.subBasins.slice(beforeCount);
  const newOrigins: Record<number, MitecoOrigin> = {};
  newSubBasins.forEach((sb, i) => {
    newOrigins[sb.subBasinCode] = { layer, basinCode: features[i].properties.basinCode };
  });
  return {
    ...current,
    loadedProject: { ...current.loadedProject, project: p },
    gisLayer: {
      type: "FeatureCollection",
      features: [...(current.gisLayer?.features ?? []), ...newLayer.features],
      linked: (current.gisLayer?.linked ?? 0) + newLayer.linked,
    },
    mitecoOrigin: { ...current.mitecoOrigin, ...newOrigins },
    mitecoMode: null,
  };
}

/**
 * Recovers permanently-deleted sub-basins (see projectEntities.ts) by
 * reimporting the full original shapefile/GeoJSON: only adds features whose
 * geometry doesn't already match one still in the project
 * (geometriesOf/geometryKey, gisLayer.ts) — avoids duplicating ones that
 * were never deleted. Recovered ones arrive without their previous
 * configuration (zone, topology, adjustments...), as warned in
 * ConfirmDeleteDialog.tsx.
 */
export function withSubBasinsRecoveredFromGis(
  current: State,
  geojson: GeoJSON.FeatureCollection,
  mapping: SubBasinColumnMapping,
  t: Translate
): State {
  if (current.phase !== "loaded") return current;
  const { project: p, newLayer } = importAdditionalSubBasins(
    current.loadedProject.project,
    geojson,
    mapping,
    t,
    geometriesOfLiveEntities(current.gisLayer, current.loadedProject.project.subBasins)
  );
  return {
    ...current,
    loadedProject: { ...current.loadedProject, project: p },
    gisLayer: {
      type: "FeatureCollection",
      features: [...(current.gisLayer?.features ?? []), ...newLayer.features],
      linked: (current.gisLayer?.linked ?? 0) + newLayer.linked,
    },
  };
}

/**
 * Creates new outlet points/aquifers from the geometries of a file uploaded
 * in LinkGisPanel.tsx that don't link to any existing one — same additive
 * criterion as `withSubBasinsRecoveredFromGis`, but without the "deleted"
 * context: here the normal case is that they're genuinely new elements, not
 * just recoveries.
 */
export function withOutletPointsCreatedFromGis(
  current: State,
  geojson: GeoJSON.FeatureCollection,
  mapping: GisColumnMapping,
  t: Translate
): State {
  if (current.phase !== "loaded") return current;
  const { project: p, newLayer } = importOutletPointsFromGeo(
    current.loadedProject.project,
    geojson,
    mapping,
    t,
    geometriesOfLiveEntities(current.outletPointsLayer, current.loadedProject.project.outletPoints)
  );
  return {
    ...current,
    loadedProject: { ...current.loadedProject, project: p },
    outletPointsLayer: {
      type: "FeatureCollection",
      features: [...current.outletPointsLayer.features, ...newLayer.features],
      linked: current.outletPointsLayer.linked + newLayer.linked,
    },
  };
}

export function withAquifersCreatedFromGis(
  current: State,
  geojson: GeoJSON.FeatureCollection,
  mapping: GisColumnMapping,
  t: Translate
): State {
  if (current.phase !== "loaded") return current;
  const { project: p, newLayer } = importAquifersFromGeo(
    current.loadedProject.project,
    geojson,
    mapping,
    t,
    geometriesOfLiveEntities(current.aquifersLayer, current.loadedProject.project.aquifers)
  );
  return {
    ...current,
    loadedProject: { ...current.loadedProject, project: p },
    aquifersLayer: {
      type: "FeatureCollection",
      features: [...current.aquifersLayer.features, ...newLayer.features],
      linked: current.aquifersLayer.linked + newLayer.linked,
    },
  };
}

// Drawing on the map (sub-basin/outlet point/aquifer): the tool is chosen in
// EditBar, clicks are picked up by SubBasinMap, and once the geometry
// closes, all that's left here is to ask for the name (pendingDrawing)
// before actually creating the entity. Reachable from card view too
// (DrawButton.tsx no longer hides itself there) — mapView: true switches
// there automatically, since there's nowhere to draw otherwise.
export function withDrawingStarted(current: State, tool: DrawingTool): State {
  return current.phase === "loaded"
    ? // mitecoMode: null — mutually exclusive with drawing, same reason as
      // withMitecoModeToggled clearing drawingMode on its own way in: both
      // bind their own click handling on the map.
      { ...current, drawingMode: tool, mitecoMode: null, mapView: true }
    : current;
}

export function withDrawingCancelled(current: State): State {
  return current.phase === "loaded"
    ? { ...current, drawingMode: null, pendingDrawing: null }
    : current;
}

export function withDrawingCompleted(
  current: State,
  geometry: GeoJSON.Point | GeoJSON.Polygon
): State {
  if (current.phase !== "loaded" || !current.drawingMode) return current;
  return { ...current, pendingDrawing: { tool: current.drawingMode, geometry } };
}

export function withDrawingNameConfirmed(
  current: State,
  name: string,
  areaKm2: number | undefined,
  t: Translate
): State {
  if (current.phase !== "loaded" || !current.pendingDrawing) return current;
  const { tool, geometry } = current.pendingDrawing;
  const project = current.loadedProject.project;

  if (tool === "subBasin" && geometry.type === "Polygon") {
    const { project: p, newLayer } = importAdditionalSubBasins(
      project,
      drawnFeature(geometry, name, areaKm2),
      { nameField: "Name", areaField: "Area" },
      t
    );
    return {
      ...current,
      loadedProject: { ...current.loadedProject, project: p },
      gisLayer: {
        type: "FeatureCollection",
        features: [...(current.gisLayer?.features ?? []), ...newLayer.features],
        linked: (current.gisLayer?.linked ?? 0) + newLayer.linked,
      },
      drawingMode: null,
      pendingDrawing: null,
    };
  }
  if (tool === "outletPoint" && geometry.type === "Point") {
    const { project: p, newLayer } = importOutletPointsFromGeo(
      project,
      drawnFeature(geometry, name),
      { nameField: "Name" },
      t
    );
    return {
      ...current,
      loadedProject: { ...current.loadedProject, project: p },
      outletPointsLayer: {
        type: "FeatureCollection",
        features: [...current.outletPointsLayer.features, ...newLayer.features],
        linked: current.outletPointsLayer.linked + newLayer.linked,
      },
      drawingMode: null,
      pendingDrawing: null,
    };
  }
  if (tool === "aquifer" && geometry.type === "Polygon") {
    const { project: p, newLayer } = importAquifersFromGeo(
      project,
      drawnFeature(geometry, name),
      { nameField: "Name" },
      t
    );
    return {
      ...current,
      loadedProject: { ...current.loadedProject, project: p },
      aquifersLayer: {
        type: "FeatureCollection",
        features: [...current.aquifersLayer.features, ...newLayer.features],
        linked: current.aquifersLayer.linked + newLayer.linked,
      },
      drawingMode: null,
      pendingDrawing: null,
    };
  }
  // Impossible combination (e.g. geometry.type doesn't match the tool) — just discard.
  return { ...current, drawingMode: null, pendingDrawing: null };
}

export function useEntityCreation(t: Translate, setState: Dispatch<SetStateAction<State>>) {
  const toggleMitecoMode = useCallback(() => {
    setState(withMitecoModeToggled);
  }, [setState]);

  const changeMitecoLayer = useCallback(
    (layer: "basins" | "subBasins") => setState((current) => withMitecoLayer(current, layer)),
    [setState]
  );

  const changeMitecoSelected = useCallback(
    (selected: Map<string, GeoJSON.Feature>) =>
      setState((current) => withMitecoSelected(current, selected)),
    [setState]
  );

  const addSelectedMiteco = useCallback(() => {
    setState((current) => withSelectedMitecoAdded(current, t));
  }, [t, setState]);

  const recoverSubBasinsFromGis = useCallback(
    (geojson: GeoJSON.FeatureCollection, mapping: SubBasinColumnMapping) => {
      setState((current) => withSubBasinsRecoveredFromGis(current, geojson, mapping, t));
    },
    [t, setState]
  );

  const createOutletPointsFromGis = useCallback(
    (geojson: GeoJSON.FeatureCollection, mapping: GisColumnMapping) => {
      setState((current) => withOutletPointsCreatedFromGis(current, geojson, mapping, t));
    },
    [t, setState]
  );

  const createAquifersFromGis = useCallback(
    (geojson: GeoJSON.FeatureCollection, mapping: GisColumnMapping) => {
      setState((current) => withAquifersCreatedFromGis(current, geojson, mapping, t));
    },
    [t, setState]
  );

  const startDrawing = useCallback(
    (tool: DrawingTool) => setState((current) => withDrawingStarted(current, tool)),
    [setState]
  );

  const cancelDrawing = useCallback(() => {
    setState(withDrawingCancelled);
  }, [setState]);

  const completeDrawing = useCallback(
    (geometry: GeoJSON.Point | GeoJSON.Polygon) =>
      setState((current) => withDrawingCompleted(current, geometry)),
    [setState]
  );

  const confirmDrawingName = useCallback(
    (name: string, areaKm2: number | undefined) => {
      setState((current) => withDrawingNameConfirmed(current, name, areaKm2, t));
    },
    [t, setState]
  );

  return {
    toggleMitecoMode,
    changeMitecoLayer,
    changeMitecoSelected,
    addSelectedMiteco,
    recoverSubBasinsFromGis,
    createOutletPointsFromGis,
    createAquifersFromGis,
    startDrawing,
    cancelDrawing,
    completeDrawing,
    confirmDrawingName,
  };
}
